// Imports
import React, { useEffect, useRef, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import styled from 'styled-components/macro';
import { fieldContainerBottomMargin } from '../../../../../../../shareables/foundation/front-end/components/forms/field-container';
import Input from '../../../../../../../shareables/foundation/front-end/components/forms/fields/input';
import Phone from '../../../../../../../shareables/foundation/front-end/components/forms/fields/phone';
import Select from '../../../../../../../shareables/foundation/front-end/components/forms/fields/select';
import MultiFieldContainer from '../../../../../../../shareables/foundation/front-end/components/forms/multi-field-container';
import { useAppDispatch } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import useUpdateOnlyEffect from '../../../../../../../shareables/foundation/front-end/utils/use-update-only-effect';
import ConditionalField from '../../../../../../../shareables/front-end/conditional-field';
import APIForm, { APIFormOptions } from '../../../../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../../../../shareables/integrations/api/redux/actions';
import {
	PostMemberFiles,
	PostMemberParameters,
} from '../../../../../../../shareables/types/api/main/website/account/member/post';
import {
	PutMemberFiles,
	PutMemberParameters,
} from '../../../../../../../shareables/types/api/main/website/account/member/put';
import { GetMembersResponse } from '../../../../../../../shareables/types/api/main/website/account/members/get';
import { GetRegistrationResponse } from '../../../../../../../shareables/types/api/main/website/settings/registration/get';
import Panel from '../../../page-types/shared-components/panel';
import AskGenderModal from '../ask-gender-modal';
import AskGenderModalNote from '../ask-gender-modal/ask-gender-modal-note';
import UploadProfilePhoto from '../upload-profile-photo';


// Styled components
const FormContainer = styled.div<{ addButtonMargin: boolean }>`
	form {
		button {
			${({ addButtonMargin }) => addButtonMargin && 'margin-top: 1.25rem;'}
		}
	}
`;

const DesignationContainer = styled.div`
	> * {
		margin-bottom: 0 !important;
	}
`;

const Wrapper = styled.div`
	> div {
		transition:
			margin-top 400ms ease,
			height 400ms ease !important;
		margin-top: 0;
		
		&.is-displayed {
			margin-top: ${fieldContainerBottomMargin};
		}
	}
`;

const UploadProfilePhotoContainer = styled.div`
	margin-top: ${fieldContainerBottomMargin};
`;

const OuterWrapper = styled.div<{ $isOpen: boolean }>`
	display: grid;
	grid-template-rows: ${({ $isOpen }) => ($isOpen ? '1fr' : '0fr')};
	transition: grid-template-rows 400ms ease;
`;

const InnerWrapper = styled.div<{ $isOpen: boolean }>`
	overflow: hidden;
	min-height: 0;
	opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
	visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
	transition:
		opacity 400ms ease,
		visibility 400ms ease;
`;


// Define the accepted props
interface PropsBase {
	storybookRegistrationSettings?: GetRegistrationResponse;
	isFormOpen: boolean;
	askGender?: boolean;
}

interface PropsStandaloneForm extends PropsBase {
	onCancel: () => void;
	onCompletion: () => void;
	member?: GetMembersResponse[0];
	
	parentParameter?: never;
}

interface PropsInForm extends PropsBase {
	parentParameter?: string;
	
	onCancel?: never;
	onCompletion?: never;
	member?: never;
}

type Props = PropsStandaloneForm | PropsInForm;


// Function component
const UpsertMember: React.FC<Props> = ({
	askGender,
	isFormOpen,
	onCancel,
	onCompletion,
	member,
	parentParameter,
	storybookRegistrationSettings,
}) => {
	// Use state
	const [designation, setDesignation] = useState(member ? (member.designation ?? 'PARTIAL') : '');
	const [removePhoto, setRemovePhoto] = useState(false);
	const [newProfilePhoto, setNewProfilePhoto] = useState<File>();
	const [avatarFirstName, setAvatarFirstName] = useState<string>(member?.firstName ?? '');
	const [avatarLastName, setAvatarLastName] = useState<string>(member?.lastName ?? '');
	const [registrationSettings, setRegistrationSettings] = useState<GetRegistrationResponse | null>(
		storybookRegistrationSettings ?? null
	);
	
	const [promotePartialMember, setPromotePartialMember] = useState(false);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use ref
	const profilePictureSetter = useRef<(file: File | null) => void>();
	const resetCanvasSource = useRef<() => void>();
	const fileRef = useRef<HTMLInputElement>(null);
	const designationSetter = useRef<(value: string | number) => void>();
	const firstNameSetter = useRef<(value: string) => void>();
	const lastNameSetter = useRef<(value: string) => void>();
	const emailSetter = useRef<(value: string) => void>();
	const phoneNumberSetter = useRef<(value: string) => void>();
	const birthdaySetter = useRef<(value: string) => void>();
	const genderSetter = useRef<(value: string | number) => void>();
	
	
	// Update on member change
	useUpdateOnlyEffect(() => {
		// Update form fields
		firstNameSetter.current?.(member?.firstName ?? '');
		lastNameSetter.current?.(member?.lastName ?? '');
		emailSetter.current?.(member?.email ?? '');
		designationSetter.current?.(member ? (member.designation ?? 'PARTIAL') : '');
		phoneNumberSetter.current?.('');
		birthdaySetter.current?.(member?.birthday ?? '');
		genderSetter.current?.('');
		
		
		// Update state
		setDesignation(member ? (member.designation ?? 'PARTIAL') : '');
		setAvatarFirstName(member?.firstName ?? '');
		setAvatarLastName(member?.lastName ?? '');
	}, [member]);
	
	
	// Load registration settings on mount
	useEffect(() => {
		if (!askGender) {
			void dispatch(
				apiActions.call<undefined, undefined, GetRegistrationResponse>({
					action: 'GET',
					uri: `/website/${window.location.hostname}/settings/registration`,
					completion: (json) => {
						setRegistrationSettings(json.response);
					},
				})
			);
		}
	}, [dispatch, askGender]);
	
	
	// If loading, then display nothing
	if (!askGender && registrationSettings === null) {
		return null;
	}
	
	
	// Build form options
	const formOptions: APIFormOptions<
		PostMemberParameters | PutMemberParameters,
		PostMemberFiles | PutMemberFiles,
		undefined
	> = {
		action: member ? 'PUT' : 'POST',
		uri: `/website/${window.location.hostname}/account/member${member ? `/${member.id}` : ''}`,
		submitButton: {
			text: member ? 'Save member' : 'Add member',
		},
		extraParameters: {
			removeProfilePhoto: member ? removePhoto : undefined,
		},
		extraFiles: {
			profilePhoto: removePhoto ? undefined : newProfilePhoto,
		},
		recoverState: !member,
	};
	
	
	// Function that handles resetting the form
	const resetForm = (handler: () => void) => {
		// Reset image form field
		profilePictureSetter.current?.(null);
		resetCanvasSource.current?.();
		
		if (fileRef.current?.value) {
			fileRef.current.value = '';
		}
		
		setRemovePhoto(false);
		
		
		// Reset form fields
		firstNameSetter.current?.(member?.firstName ?? '');
		lastNameSetter.current?.(member?.lastName ?? '');
		emailSetter.current?.(member?.email ?? '');
		designationSetter.current?.(member ? (member.designation ?? 'PARTIAL') : '');
		phoneNumberSetter.current?.('');
		birthdaySetter.current?.(member?.birthday ?? '');
		genderSetter.current?.('');
		
		
		// Reset state
		setDesignation(member ? (member.designation ?? 'PARTIAL') : '');
		setAvatarFirstName(member?.firstName ?? '');
		setAvatarLastName(member?.lastName ?? '');
		
		
		// Handle callback
		handler();
	};
	
	
	// Handle designation update
	const handleDesignationChange = (newDesignation: string | number) => {
		if (typeof newDesignation === 'string') {
			setDesignation(newDesignation);
		}
	};
	
	
	// Handle a change to the form's values
	const handleChange = (values: Partial<PutMemberParameters>) => {
		setPromotePartialMember(Boolean(values.email));
	};
	
	
	// Initialize
	const showLastName = designation !== 'Child' && designation !== 'Pet';
	
	
	// Function that decides if a field should be displayed
	const shouldDisplay = (condition = true) => isFormOpen && condition;
	
	
	// Build fields
	const fields = (
		<Wrapper>
			<DesignationContainer>
				<Select
					name={parentParameter ? `${parentParameter}.designation` : 'designation'}
					label='This member is a…'
					initialValue={designation === '' ? undefined : designation}
					placeholder='Please select…'
					disabled={Boolean(member)}
					exclude={!isFormOpen || Boolean(member)}
					required={isFormOpen && !member}
					onChange={(newDesignation) => handleDesignationChange(newDesignation)}
					bindSetter={(setter) => (designationSetter.current = setter)}
				>
					<option value='JOINT'>Joint member</option>
					<option value='PARTIAL'>Joint member (no email)</option>
					<option value='Child'>Child</option>
					<option value='Pet'>Pet</option>
					<option value='Relative'>Relative</option>
					<option value='Other'>Other</option>
				</Select>
				<AnimateHeight
					duration={400}
					animateOpacity={true}
					height={shouldDisplay(Boolean(member) || (designation !== 'JOINT' && designation !== '')) ? 'auto' : 0}
				>
					<small>
						<em>
							This type of member is shown in the directory, but won’t be able to sign in or receive communications
						</em>
					</small>
				</AnimateHeight>
			</DesignationContainer>
			
			<ConditionalField isDisplayed={shouldDisplay(designation !== '')}>
				<MultiFieldContainer growAll>
					<Input
						name={parentParameter ? `${parentParameter}.firstName` : 'firstName'}
						exclude={!shouldDisplay()}
						disabled={!shouldDisplay()}
						label='First name'
						placeholder='John'
						type='text'
						initialValue={avatarFirstName}
						maxLength={50}
						required={shouldDisplay()}
						onChange={(newFirstName) => setAvatarFirstName(newFirstName)}
						bindSetter={(setter) => (firstNameSetter.current = setter)}
					/>
					
					{showLastName && (
						<Input
							name={parentParameter ? `${parentParameter}.lastName` : 'lastName'}
							label='Last name'
							placeholder='Doe'
							type='text'
							initialValue={avatarLastName}
							maxLength={50}
							exclude={!shouldDisplay(showLastName)}
							disabled={!shouldDisplay(showLastName)}
							required={shouldDisplay(showLastName)}
							onChange={(newLastName) => setAvatarLastName(newLastName)}
							bindSetter={(setter) => (lastNameSetter.current = setter)}
						/>
					)}
				</MultiFieldContainer>
			</ConditionalField>
			
			<ConditionalField
				isDisplayed={shouldDisplay(designation === 'JOINT' || (member ? designation === 'PARTIAL' : false))}
			>
				<Input
					name={parentParameter ? `${parentParameter}.email` : 'email'}
					label='Email address'
					note={member ? 'providing this will allow them to sign in' : undefined}
					placeholder='you@example.com'
					type='email'
					required={shouldDisplay(designation === 'JOINT')}
					exclude={!shouldDisplay(designation === 'JOINT' || (member ? designation === 'PARTIAL' : false))}
					disabled={!shouldDisplay(designation === 'JOINT' || (member ? designation === 'PARTIAL' : false))}
					maxLength={75}
					bindSetter={(setter) => (emailSetter.current = setter)}
				/>
			</ConditionalField>
			
			<ConditionalField
				isDisplayed={shouldDisplay(
					designation === 'JOINT' || (member ? designation === 'PARTIAL' && promotePartialMember : false)
				)}
			>
				<Phone
					name={parentParameter ? `${parentParameter}.phone` : 'phone'}
					label='Cell phone'
					exclude={
						!shouldDisplay(
							designation === 'JOINT' || (member ? designation === 'PARTIAL' && promotePartialMember : false)
						)
					}
					maxLength={20}
					bindSetter={(setter) => (phoneNumberSetter.current = setter)}
				/>
			</ConditionalField>
			
			<ConditionalField
				isDisplayed={shouldDisplay(
					(designation === 'JOINT' || promotePartialMember) &&
						(Boolean(askGender) || Boolean(registrationSettings?.askGender))
				)}
			>
				<Select
					name={parentParameter ? `${parentParameter}.gender` : 'gender'}
					label='Gender'
					placeholder='Please select…'
					exclude={
						!shouldDisplay(
							(designation === 'JOINT' || promotePartialMember) &&
								(Boolean(askGender) || Boolean(registrationSettings?.askGender))
						)
					}
					disabled={
						!shouldDisplay(
							(designation === 'JOINT' || promotePartialMember) &&
								(Boolean(askGender) || Boolean(registrationSettings?.askGender))
						)
					}
					note={<AskGenderModalNote />}
					bindSetter={(setter) => (genderSetter.current = setter)}
				>
					<option value='M'>Male</option>
					<option value='F'>Female</option>
					<option value='NS'>Prefer not to say</option>
				</Select>
			</ConditionalField>
			
			<ConditionalField
				isDisplayed={shouldDisplay(Boolean(onCancel) && (designation === 'Child' || designation === 'Pet'))}
			>
				<Input
					name={parentParameter ? `${parentParameter}.birthday` : 'birthday'}
					label='Birthday'
					placeholder='Click to select date…'
					exclude={!shouldDisplay(Boolean(onCancel) && (designation === 'Child' || designation === 'Pet'))}
					disabled={!shouldDisplay(Boolean(onCancel) && (designation === 'Child' || designation === 'Pet'))}
					type='date'
					note={designation === 'Child' ? '(optional; used for calculating age, never displayed)' : undefined}
					bindSetter={(setter) => (birthdaySetter.current = setter)}
				/>
			</ConditionalField>
			
			<AnimateHeight
				duration={400}
				animateOpacity={true}
				height={shouldDisplay(Boolean(onCancel) && (member !== undefined || designation !== '')) ? 'auto' : 0}
			>
				<UploadProfilePhotoContainer>
					<UploadProfilePhoto
						setNewProfilePhoto={setNewProfilePhoto}
						setRemovePhoto={setRemovePhoto}
						removePhoto={removePhoto}
						profilePictureSetter={profilePictureSetter}
						resetCanvasSource={resetCanvasSource}
						fileRef={fileRef}
						profilePhotoURL={member?.profilePhotoURL}
						memberFirstName={avatarFirstName}
						memberLastName={avatarLastName}
						defaultAvatarSize='HALF'
						useDefaultColor
						parentParameter={parentParameter}
					/>
				</UploadProfilePhotoContainer>
			</AnimateHeight>
		</Wrapper>
	);
	
	
	// Return JSX
	return (
		<React.Fragment>
			{onCancel ? (
				<OuterWrapper $isOpen={isFormOpen} aria-hidden={!isFormOpen}>
					<InnerWrapper $isOpen={isFormOpen}>
						<Panel closeOnClick={() => resetForm(onCancel)}>
							<FormContainer addButtonMargin={designation === ''}>
								<APIForm
									options={formOptions}
									onCancel={() => resetForm(onCancel)}
									onCompletion={() => resetForm(onCompletion)}
									onChange={handleChange}
								>
									{fields}
								</APIForm>
							</FormContainer>
						</Panel>
					</InnerWrapper>
				</OuterWrapper>
			) : (
				fields
			)}
			
			<AskGenderModal />
		</React.Fragment>
	);
};

export default UpsertMember;
